import {useFormContext, useFormState} from '@yandex-delivery/form'
import {get, isFilled, map, pipe, toDefaultZero} from '@yandex-delivery/utils'
import {useUpdateEffect} from '@yandex-delivery/utils/hooks'
import {useCallback, useMemo, useState} from 'react'

import type {DestinationPoint, OptimizeRouteResultResponse, SourcePoint} from 'src/shared/types'

import {useFetchOptimizedRoute} from './hooks/use-fetch-optimized-route'
import {useOptimizeRouteConfirmModal} from './OptimizeRouteConfirmModal/hooks'
import {useOptimizeRouteCustomModal} from './OptimizeRouteCustomModal/hooks'
import type {RouteOptimizationState} from './types'
import {toDestinationRouteChangePoint, toSourceRouteChangePoint} from './utils'

function useUpdateDestinationPoints() {
  const {reset, watch, resetField} = useFormContext()
  const destinationPoints: DestinationPoint[] = watch('destinationPoints')

  const destinationPointsUpdateHandler = useCallback(
    (newRoute?: string[]) => {
      if (newRoute === undefined) {
        return
      }

      const points = newRoute
        .slice(1)
        .map(pointId => destinationPoints.find(point => point._id.toString() === pointId))
        .filter(isFilled)

      reset(
        formValues => ({
          ...formValues,
          destinationPoints: points,
        }),
        {
          keepErrors: true,
          keepDirty: true,
          keepTouched: true,
          keepSubmitCount: false,
        },
      )

      resetField('destinationPoints', {
        keepTouched: false,
      })
    },
    [destinationPoints, reset, resetField],
  )

  return {
    destinationPointsUpdateHandler,
  } as const
}

export function useRouteChange() {
  const {watch} = useFormContext()
  const {touchedFields} = useFormState()
  const {destinationPointsUpdateHandler} = useUpdateDestinationPoints()
  const [draftOptimizationState, setDraftOptimizationState] = useState<RouteOptimizationState>('idle')
  const [optimizationState, setOptimizationState] = useState<RouteOptimizationState>('idle')
  const [routeOrder, setRouteOrder] = useState<string[]>()
  const {fetchOptimisedRouteHandler} = useFetchOptimizedRoute()

  const optimizeRouteConfirmModal = useOptimizeRouteConfirmModal()
  const optimizeRouteCustomModal = useOptimizeRouteCustomModal()

  const sourcePoint: SourcePoint = watch('sourcePoint')
  const destinationPoints: DestinationPoint[] = watch('destinationPoints')

  /**
   * @BusinessLogic:
   * Необходимо учитывать, сколько точек было потрогано руками,
   * чтобы при изменении маршрута, не сбрасывать состояние оптимизации
   */
  const touchedDestinationPointsCount = pipe(
    touchedFields,
    get('destinationPoints'),
    map(get('address.coordinates')),
    get('length'),
    toDefaultZero,
  )

  const points = useMemo(
    () =>
      routeOrder
        ? routeOrder
            .slice(1)
            .map(pointId => destinationPoints.find(destination => destination._id.toString() === pointId))
            .filter(isFilled)
        : destinationPoints,
    [destinationPoints, routeOrder],
  )

  const route = useMemo(
    () => [toSourceRouteChangePoint(sourcePoint), ...points.map(toDestinationRouteChangePoint)],
    [points, sourcePoint],
  )

  const successFetchManualOptimizationRouteHandler = useCallback((routeResp?: OptimizeRouteResultResponse) => {
    setRouteOrder(routeResp?.result?.route)
    setDraftOptimizationState('completed')
  }, [])

  const successFetchOptimizationRouteHandler = useCallback(
    (routeResp?: OptimizeRouteResultResponse) => {
      destinationPointsUpdateHandler(routeResp?.result?.route)
      setOptimizationState('completed')
      setDraftOptimizationState('completed')
      optimizeRouteConfirmModal.closeOptimizeRouteConfirmModalHandler()
    },
    [destinationPointsUpdateHandler, optimizeRouteConfirmModal],
  )

  const saveNewOrderRouteHandler = useCallback(() => {
    destinationPointsUpdateHandler(routeOrder)
    setOptimizationState(draftOptimizationState)
    setRouteOrder(undefined)
    optimizeRouteCustomModal.closeOptimizeRouteCustomModalHandler()
  }, [destinationPointsUpdateHandler, draftOptimizationState, optimizeRouteCustomModal, routeOrder])

  const resetNewOrderRouteHandler = useCallback(() => {
    setDraftOptimizationState(optimizationState)
    setRouteOrder(undefined)
    optimizeRouteCustomModal.closeOptimizeRouteCustomModalHandler()
  }, [optimizationState, optimizeRouteCustomModal])

  const dragEndHandler = useCallback((result: string[]) => {
    setRouteOrder(result)
    setDraftOptimizationState('idle')
  }, [])

  const saveOptimizedRouteHandler = useCallback(() => {
    fetchOptimisedRouteHandler(
      {
        route,
      },
      {
        onPending: () => {
          setOptimizationState('pending')
        },
        onError: () => {
          setOptimizationState('error')
        },
        onSuccess: successFetchOptimizationRouteHandler,
      },
    )
  }, [fetchOptimisedRouteHandler, route, successFetchOptimizationRouteHandler])

  const optimizeRouteHandler = useCallback(() => {
    fetchOptimisedRouteHandler(
      {
        route,
      },
      {
        onPending: () => {
          setDraftOptimizationState('pending')
        },
        onError: () => {
          setDraftOptimizationState('error')
        },
        onSuccess: successFetchManualOptimizationRouteHandler,
      },
    )
  }, [fetchOptimisedRouteHandler, route, successFetchManualOptimizationRouteHandler])

  useUpdateEffect(() => {
    if (touchedDestinationPointsCount === 0) {
      return
    }

    setOptimizationState('idle')
    setDraftOptimizationState('idle')
  }, [touchedDestinationPointsCount])

  return {
    sourcePoint,
    destinationPoints,
    route,
    routeOrder,
    dragEndHandler,
    resetNewOrderRouteHandler,
    optimizeRouteHandler,
    saveNewOrderRouteHandler,
    saveOptimizedRouteHandler,
    optimizationState,
    draftOptimizationState,
    ...optimizeRouteConfirmModal,
    ...optimizeRouteCustomModal,
  }
}
